import React, { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import { useSearchParams } from"react-router-dom";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { 
 Receipt, 
 Plus,
 Search,
 Edit,
 Trash2,
 Calendar,
 DollarSign,
 Plane,
 Heart,
 Gift,
 Building2,
 TrendingUp,
 Filter
} from"lucide-react";
import { toast } from"sonner";

const OtherExpensesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [searchParams] = useSearchParams();
 const [loading, setLoading] = useState(true);
 const [expenses, setExpenses] = useState([]);
 const [summary, setSummary] = useState(null);
 const [searchTerm, setSearchTerm] = useState("");
 const [filterCategory, setFilterCategory] = useState("all");
 const [showDialog, setShowDialog] = useState(false);
 const [editingExpense, setEditingExpense] = useState(null);
 const [formData, setFormData] = useState({
 title:"",
 description:"",
 category:"travel",
 amount:"",
 currency:"EUR",
 expense_date: new Date().toISOString().split('T')[0],
 vendor:"",
 payment_method:"bank_transfer",
 payment_status:"paid",
 employee_name:"",
 is_recurring: false,
 recurring_frequency:"",
 notes:""
 });

 const categories = [
 { value:"travel", label:"Travel", icon: Plane, color:"blue"},
 { value:"holidays", label:"Holidays/Events", icon: Calendar, color:"purple"},
 { value:"philanthropic", label:"Philanthropic/Charity", icon: Heart, color:"red"},
 { value:"gifts", label:"Gifts", icon: Gift, color:"pink"},
 { value:"office_supplies", label:"Office Supplies", icon: Building2, color:"slate"},
 { value:"utilities", label:"Utilities", icon: Building2, color:"yellow"},
 { value:"marketing", label:"Marketing", icon: TrendingUp, color:"green"},
 { value:"insurance", label:"Insurance", icon: Receipt, color:"indigo"},
 { value:"subscriptions", label:"Subscriptions", icon: Receipt, color:"cyan"},
 { value:"other", label:"Other", icon: Receipt, color:"slate"}
 ];

 useEffect(() => {
 loadData();
 if (searchParams.get("action") ==="add") {
 setShowDialog(true);
 }
 }, []);

 const loadData = async () => {
 setLoading(true);
 try {
 const [expRes, summaryRes] = await Promise.all([
 axios.get("/other-expenses"),
 axios.get("/other-expenses/summary/overview")
 ]);
 setExpenses(expRes.data);
 setSummary(summaryRes.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleSubmit = async () => {
 if (!formData.title || !formData.amount || !formData.category) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 const payload = {
 ...formData,
 amount: parseFloat(formData.amount),
 expense_date: new Date(formData.expense_date).toISOString()
 };

 if (editingExpense) {
 await axios.patch(`/other-expenses/${editingExpense.id}`, payload);
 toast.success(t("messages.updateSuccess"));
 } else {
 await axios.post("/other-expenses", payload);
 toast.success(t("messages.saveSuccess"));
 }
 setShowDialog(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to save expense");
 }
 };

 const handleDelete = async (id) => {
 if (!window.confirm("Are you sure you want to delete this expense?")) return;
 
 try {
 await axios.delete(`/other-expenses/${id}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const openEditDialog = (expense) => {
 setEditingExpense(expense);
 setFormData({
 title: expense.title ||"",
 description: expense.description ||"",
 category: expense.category ||"travel",
 amount: expense.amount?.toString() ||"",
 currency: expense.currency ||"EUR",
 expense_date: expense.expense_date ? expense.expense_date.split('T')[0] :"",
 vendor: expense.vendor ||"",
 payment_method: expense.payment_method ||"bank_transfer",
 payment_status: expense.payment_status ||"paid",
 employee_name: expense.employee_name ||"",
 is_recurring: expense.is_recurring || false,
 recurring_frequency: expense.recurring_frequency ||"",
 notes: expense.notes ||""
 });
 setShowDialog(true);
 };

 const resetForm = () => {
 setEditingExpense(null);
 setFormData({
 title:"",
 description:"",
 category:"travel",
 amount:"",
 currency:"EUR",
 expense_date: new Date().toISOString().split('T')[0],
 vendor:"",
 payment_method:"bank_transfer",
 payment_status:"paid",
 employee_name:"",
 is_recurring: false,
 recurring_frequency:"",
 notes:""
 });
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0
 }).format(amount || 0);
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return"-";
 return new Date(dateStr).toLocaleDateString();
 };

 const getCategoryBadge = (category) => {
 const cat = categories.find(c => c.value === category) || categories[categories.length - 1];
 const colorClasses = {
 blue:"bg-blue-500/10 text-blue-700",
 purple:"bg-violet-500/10 text-violet-700",
 red:"bg-rose-500/10 text-rose-700",
 pink:"bg-pink-100 text-pink-800",
 slate:"bg-white/60 text-slate-800",
 yellow:"bg-amber-500/10 text-amber-700",
 green:"bg-emerald-500/10 text-emerald-700",
 indigo:"bg-indigo-100 text-indigo-800",
 cyan:"bg-cyan-100 text-cyan-800"
 };
 return <Badge className={colorClasses[cat.color]}>{cat.label}</Badge>;
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case"paid":
 return <Badge className="bg-emerald-500/10 text-emerald-700">Paid</Badge>;
 case"pending":
 return <Badge className="bg-amber-500/10 text-amber-700">Pending</Badge>;
 case"reimbursed":
 return <Badge className="bg-blue-500/10 text-blue-700">Reimbursed</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 const filteredExpenses = expenses.filter(exp => {
 const matchesSearch = 
 exp.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 exp.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 exp.description?.toLowerCase().includes(searchTerm.toLowerCase());
 const matchesCategory = filterCategory ==="all"|| exp.category === filterCategory;
 return matchesSearch && matchesCategory;
 });

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div className="flex justify-between items-start">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 Other Expenses
 </h1>
 <p className="text-slate-600 mt-1">
 Track non-operational company expenses
 </p>
 </div>
 <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-expense-btn">
 <Plus className="w-4 h-4 mr-2"/>
 Add Expense
 </Button>
 </div>

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-4 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600">Total Expenses</p>
 <p className="text-2xl font-bold text-slate-900">{summary.total_expenses}</p>
 </div>
 <Receipt className="w-8 h-8 text-slate-400"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-blue-600">Total Amount</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_amount)}</p>
 </div>
 <DollarSign className="w-8 h-8 text-blue-400"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-green-600">This Month</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.this_month)}</p>
 </div>
 <Calendar className="w-8 h-8 text-green-400"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-purple-600">This Year</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.this_year)}</p>
 </div>
 <TrendingUp className="w-8 h-8 text-purple-400"/>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Category Breakdown */}
 {summary?.by_category && Object.keys(summary.by_category).length > 0 && (
 <Card className="">
 <CardHeader>
 <CardTitle className="text-lg">Expenses by Category</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex flex-wrap gap-3">
 {Object.entries(summary.by_category).map(([cat, data]) => {
 const catInfo = categories.find(c => c.value === cat) || categories[categories.length - 1];
 return (
 <div key={cat} className="flex items-center gap-2 px-3 py-2 bg-white/50 rounded-sm">
 {getCategoryBadge(cat)}
 <span className="font-semibold text-slate-900">{formatCurrency(data.amount)}</span>
 <span className="text-sm text-slate-500">({data.count})</span>
 </div>
 );
 })}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Filters */}
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex gap-4">
 <div className="flex-1">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4"/>
 <Input
 placeholder="Search expenses..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 <Select value={filterCategory} onValueChange={setFilterCategory}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Category"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Categories</SelectItem>
 {categories.map(cat => (
 <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </CardContent>
 </Card>

 {/* Expenses List */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Receipt className="w-5 h-5 text-indigo-600"/>
 Expenses ({filteredExpenses.length})
 </CardTitle>
 </CardHeader>
 <CardContent>
 {filteredExpenses.length === 0 ? (
 <div className="text-center py-12">
 <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600">No expenses found</p>
 <Button className="mt-4"onClick={() => { resetForm(); setShowDialog(true); }}>
 <Plus className="w-4 h-4 mr-2"/>
 Add First Expense
 </Button>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-slate-200">
 <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Date</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Title</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Category</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Vendor</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Amount</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
 </tr>
 </thead>
 <tbody>
 {filteredExpenses.map((exp) => (
 <tr key={exp.id} className="border-b border-white/40 hover:bg-white/80">
 <td className="py-3 px-4 text-sm text-slate-900">{formatDate(exp.expense_date)}</td>
 <td className="py-3 px-4">
 <p className="font-medium text-slate-900">{exp.title}</p>
 {exp.description && <p className="text-[11px] text-slate-500">{exp.description}</p>}
 </td>
 <td className="py-3 px-4">{getCategoryBadge(exp.category)}</td>
 <td className="py-3 px-4 text-sm text-slate-500">{exp.vendor ||"-"}</td>
 <td className="py-3 px-4 text-right font-semibold text-slate-900">
 {formatCurrency(exp.amount, exp.currency)}
 </td>
 <td className="py-3 px-4 text-center">{getStatusBadge(exp.payment_status)}</td>
 <td className="py-3 px-4 text-right">
 <div className="flex justify-end gap-2">
 <Button variant="ghost"size="sm"onClick={() => openEditDialog(exp)}>
 <Edit className="w-4 h-4"/>
 </Button>
 <Button variant="ghost"size="sm"className="text-red-600 hover:text-red-700"onClick={() => handleDelete(exp.id)}>
 <Trash2 className="w-4 h-4"/>
 </Button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Add/Edit Expense Dialog */}
 <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>{editingExpense ?"Edit Expense":"Add New Expense"}</DialogTitle>
 </DialogHeader>
 <div className="grid grid-cols-2 gap-4 py-4">
 <div className="col-span-2">
 <Label>Title *</Label>
 <Input 
 value={formData.title} 
 onChange={(e) => setFormData({...formData, title: e.target.value})} 
 placeholder="e.g., Flight to Paris for conference"
 />
 </div>
 <div className="col-span-2">
 <Label>Description</Label>
 <Textarea 
 value={formData.description} 
 onChange={(e) => setFormData({...formData, description: e.target.value})} 
 placeholder="Additional details..."
 rows={2}
 />
 </div>
 <div>
 <Label>Category *</Label>
 <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 {categories.map(cat => (
 <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Expense Date</Label>
 <Input 
 type="date"
 value={formData.expense_date} 
 onChange={(e) => setFormData({...formData, expense_date: e.target.value})} 
 />
 </div>
 <div>
 <Label>Amount *</Label>
 <Input 
 type="number"
 value={formData.amount} 
 onChange={(e) => setFormData({...formData, amount: e.target.value})} 
 placeholder="0.00"
 />
 </div>
 <div>
 <Label>Currency</Label>
 <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="EUR">EUR</SelectItem>
 <SelectItem value="USD">USD</SelectItem>
 <SelectItem value="MKD">MKD</SelectItem>
 <SelectItem value="ALL">ALL</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Vendor</Label>
 <Input 
 value={formData.vendor} 
 onChange={(e) => setFormData({...formData, vendor: e.target.value})} 
 placeholder="Who was paid"
 />
 </div>
 <div>
 <Label>Payment Method</Label>
 <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
 <SelectItem value="card">Card</SelectItem>
 <SelectItem value="cash">Cash</SelectItem>
 <SelectItem value="check">Check</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Payment Status</Label>
 <Select value={formData.payment_status} onValueChange={(v) => setFormData({...formData, payment_status: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="paid">Paid</SelectItem>
 <SelectItem value="pending">Pending</SelectItem>
 <SelectItem value="reimbursed">Reimbursed</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Employee (if applicable)</Label>
 <Input 
 value={formData.employee_name} 
 onChange={(e) => setFormData({...formData, employee_name: e.target.value})} 
 placeholder="Who incurred the expense"
 />
 </div>
 <div className="col-span-2">
 <Label>Notes</Label>
 <Textarea 
 value={formData.notes} 
 onChange={(e) => setFormData({...formData, notes: e.target.value})} 
 placeholder="Additional notes..."
 rows={2}
 />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline"onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
 <Button onClick={handleSubmit}>{editingExpense ?"Save Changes":"Add Expense"}</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default OtherExpensesPage;
